
import {
  composePaths,
  createDefaultValue,
  JsonFormsRendererRegistryEntry,
  rankWith,
  ControlElement,
  schemaTypeIs,
  update,
} from "@jsonforms/core";
import {
  DispatchRenderer,
  rendererProps,
  useJsonFormsArrayControl,
} from "@jsonforms/vue";
import { defineComponent, inject, markRaw, ref } from "vue";
import { ElTag } from "element-plus";
import { useVanillaArrayControl } from "@jsonforms/vue-vanilla";
import ArrayListElement from "@/core/json-forms/controls/ArrayListElement.vue";
import FunctionSelector from "@/core/json-forms/components/FunctionSelector.vue";
import RendererConfigurations from "@/core/json-forms/renderer-configurations";
import i18n from "@/core/i18n/i18n";
import {
  ARRAY_INJECTION_ENABLED_KEY,
  buildInjectedArrayItem,
  getInjectedArrayLabel,
  parseInjectedArray,
} from "@/core/json-forms/components/arrayInjection";

const controlRenderer = defineComponent({
  name: RendererConfigurations.ArrayListRenderer.name,
  components: {
    ArrayListElement,
    DispatchRenderer,
    ElTag,
    FunctionSelector,
  },
  props: {
    outputList: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    userDefinedVariables: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    systemVariables: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    metaoutputList: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    expressions: {
      required: false,
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    stepIdx: Number,
    ...rendererProps<ControlElement>(),
  },
  emits: ["handleClickEvent", "expressionDrawer"],
  data() {
    return {
      toggle: true,
      drawerVisible: false,
      formElement: null as HTMLElement | null,
    };
  },
  setup(props: any) {
    return {
      ...useVanillaArrayControl(useJsonFormsArrayControl(props)),
      dispatch: inject("dispatch") as any,
      injectionEnabled: inject(ARRAY_INJECTION_ENABLED_KEY, ref(false)),
      t: i18n.global.t,
    };
  },
  computed: {
    RendererConfigurations() {
      return RendererConfigurations;
    },
    noData(): boolean {
      return !this.control.data || this.control.data.length === 0;
    },
    // Only a list of scalar entries can be replaced by one variable; a list of objects or of lists
    // keeps its own editors.
    injectable(): boolean {
      const itemType = (this.control.schema as any)?.type;
      return (
        !!this.injectionEnabled && itemType !== "object" && itemType !== "array"
      );
    },
    injectedReference(): any {
      return this.injectable ? parseInjectedArray(this.control.data) : null;
    },
    injectedLabel(): string {
      const stepNumbers = new Map<number, number>();
      [...this.outputList, ...this.metaoutputList].forEach((item: any) => {
        if (typeof item?.stepNumber === "number" && item.stepNumber > 0) {
          stepNumbers.set(item.index, item.stepNumber);
        }
      });
      return getInjectedArrayLabel(this.injectedReference, stepNumbers);
    },
  },
  mounted() {
    const formElement = document.getElementById(
      `json-form-component${this.stepIdx}`
    );
    if (formElement) {
      this.formElement = markRaw(formElement);
      this.formElement.addEventListener(
        "click",
        this.closeDrawerOnOutsideClick
      );
    }
  },
  beforeUnmount() {
    if (this.formElement) {
      this.formElement.removeEventListener(
        "click",
        this.closeDrawerOnOutsideClick
      );
      this.formElement = null;
    }
    if (this.drawerVisible) {
      this.setDrawerVisible(false);
    }
  },
  methods: {
    setDrawerVisible(visible: boolean) {
      this.drawerVisible = visible;
      this.$emit("handleClickEvent", visible, this.control.id);
    },
    toggleInjectionDrawer() {
      this.setDrawerVisible(!this.drawerVisible);
    },
    closeDrawerOnOutsideClick(event: Event) {
      if (!this.drawerVisible) {
        return;
      }
      const target = event.target as Node;
      const inside = [
        document.getElementById(`${this.control.id}-popper`),
        document.getElementById(`${this.control.id}-inject-btn`),
      ].some((element) => element?.contains(target));
      if (!inside) {
        this.setDrawerVisible(false);
      }
    },
    // The step editor flags a step as modified from the bubbling `input` event of a typed field;
    // a dispatched data update raises none, so an injection would never be saved by a run.
    markChanged() {
      this.$nextTick(() => {
        if (this.$el instanceof HTMLElement) {
          this.$el.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
    },
    inject(type: any, event: any) {
      this.dispatch(
        update(this.control.path, () => [buildInjectedArrayItem(type, event)])
      );
      this.markChanged();
      this.setDrawerVisible(false);
    },
    onAddOutput(event: any) {
      if (event.stepIndex > -1) {
        this.inject("output", event);
      }
    },
    onAddMetaoutput(event: any) {
      if (event.stepIndex > -1) {
        this.inject("metaoutput", event);
      }
    },
    onAddUserVariable(event: any) {
      this.inject("user-defined-variable", event);
    },
    removeInjection() {
      this.dispatch(update(this.control.path, () => []));
      this.markChanged();
    },
    composePaths,
    createDefaultValue,
    addButtonClick() {
      this.addItem(
        this.control.path,
        createDefaultValue(this.control.schema)
      )();
      this.toggle = true;
    },
    handleClickEvent(visible, controlId) {
      this.$emit("handleClickEvent", visible, controlId);
    },
    expressionDrawer(visible) {
      this.$emit("expressionDrawer", visible);
    },
  },
});

export default controlRenderer;

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: rankWith(15, schemaTypeIs("array")),
};
